import { Avatar, Box, ClickAwayListener, Paper, Popper, Tooltip, Typography } from "@mui/material";
import CustomDropdown from "../Common/CustomDropdown";
import DropdownBox from "../Common/DropdownBox";
import UserAvatar from "../Common/UserAvatar";
import { useState } from "react";
import { getAvatarColor, getAvatarInitials } from "../../utils/utils";
import "../../styles/actionItems.css";

interface UserSelectorProps {
  value: string | string[] | null;
  onChange: (value: string | string[] | null, removeField?: string) => void;
  users: any[];
  disabled?: boolean;
  error?: boolean;
  placeholder?: string;
  variant?: "default" | "icon" | "avatarOnly";
  variantIcon?: string;
  tooltipTitle?: string;
  multi?: boolean;
  spaces?: any[];
  spacesValue?: string[] | null;
  onSpacesChange?: (spaceIds: string[] | null) => void;
  position?: boolean;
  removeField?: string;
  triggerRef?: React.RefObject<HTMLDivElement | null>;
}

const UserSelector: React.FC<UserSelectorProps> = ({
  value,
  onChange,
  users,
  disabled,
  error,
  placeholder = "Search users...",
  variant = "default",
  variantIcon,
  tooltipTitle,
  multi = false,
  spaces,
  spacesValue,
  onSpacesChange,
  position= false,
  removeField,
  triggerRef,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const items = [
    ...(users?.map(u => ({ ...u, type: 'user' })) || []),
    ...(spaces?.map(s => ({
      type: 'space',
      id: s.spaceId,
      name: s.spaceName,
      members: s.spaceMembers,
    })) || [])
  ];
  const getItemKey = (item: any) => {
    if (item.type === "user") return item?.profileOrgRelationshipId;
    if (item.type === "space") return item?.id;
    return item?.id || item?.profileOrgRelationshipId;
  };
  const selectedItems = multi
  ? items.filter((i) => {
      const key = getItemKey(i);
      if (i.type === "user") return Array.isArray(value) && value.includes(key);
      if (i.type === "space") return Array.isArray(spacesValue) && spacesValue.includes(key);
      return false;
    })
  : value
  ? [items.find((i) => getItemKey(i) === value)!]
  : [];

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    if (!disabled) setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  /** SHARED DROPDOWN (used in both variants) */
  const dropdown = (
    <Popper
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      placement={  position ? "top-start" : "bottom-start"}
      popperOptions={{
        modifiers: [
          {
            name: "flip",
            enabled: true,
            options: {
              fallbackPlacements: ["top-start", "top"],
              padding: 8,
            }
          },
          {
            name: "preventOverflow",
            enabled: true,
            options: {
              boundary: "window",
              tether: false,
              padding: 8,
            }
          }
        ]
      }}
      sx={{zIndex: 1300}}
    >
      <ClickAwayListener disableReactTree onClickAway={handleClose}>
      <Paper 
        onClick={(e) => e.stopPropagation()}
        sx={{ maxHeight: 300, minWidth: 220, overflow: "auto", borderRadius: 1 }}>
        <CustomDropdown
          items={items}
          value={multi ? selectedItems : selectedItems[0] || null}
          onChange={(selected: any | any[]) => {
            if (!multi) {
              if (selected.type === "user") onChange(getItemKey(selected));
              else if (selected.type === "space") onSpacesChange?.([getItemKey(selected)]);
              handleClose();
            } else {
              const usersSelected: string[] = [];
              const spacesSelected: string[] = [];
              (selected as any[]).forEach((item) => {
                if (item.type === "user") usersSelected.push(item.profileOrgRelationshipId);
                if (item.type === "space") spacesSelected.push(item.id);
              });
              onChange(usersSelected);
              onSpacesChange?.(spacesSelected);
            }
          }}
          getKey={getItemKey}
          placeholder={placeholder}
          multiple={multi}
          isSubscriber={multi ? true : false}
          groupBy={(item) => item.type === 'space' ? 'Spaces' : 'People'}
          renderItem={(item) => (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {item.type === "user" ? (
                <Avatar
                  src={item.pictureUrl || item.avatar_url || item.picture}
                  alt={item.name ?? item.email}
                  sx={{
                    bgcolor: !item.pictureUrl ? getAvatarColor(item.email || item.name) : undefined,
                    width: 26,
                    height: 26,
                    fontSize: 10,
                  }}
                >
                  {!item.pictureUrl && getAvatarInitials(item.email || item.name)}
                </Avatar>
              ) : (
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: "4px",
                    bgcolor: getAvatarColor(item.name),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 10,
                    color: "#ffff",
                  }}
                >
                  {getAvatarInitials(item.name)}
                </Box>
              )}
              <Typography sx={{ fontSize: 13 }}>
                {item.name || item.title || item.email}
              </Typography>
            </Box>
          )}
          filterFn={(u, search) =>
            (u.name || "").toLowerCase().includes((search || "").toLowerCase()) ||
            (u.email || "").toLowerCase().includes((search || "").toLowerCase())
          }
          onClose={handleClose}
        />
      </Paper>
      </ClickAwayListener>
      </Popper>
    );

  /** VARIANT: AVATAR_ONLY (used in compact table cells) */
  if (variant === "avatarOnly") {
    const selected = selectedItems[0];
    const avatarContent = selected ? (
      <Tooltip title={selected.name || selected.email || "Assignee"} arrow placement="top">
        <Avatar
          src={selected.pictureUrl || selected.avatar_url || selected.picture || undefined}
          alt={selected.name ?? selected.email}
          className={`user-selector-avatar ${disabled ? "disabled" : ""}`}
          sx={{
            bgcolor: !selected.pictureUrl 
              ? getAvatarColor(selected.email || selected.name || "")
              : undefined,
          }}
        >
          {!selected.pictureUrl ? getAvatarInitials(selected.email || selected.name) : null}
        </Avatar>
      </Tooltip>
    ) : (
      <Tooltip title="Select Assignee" arrow placement="top">
        <Avatar
          className={`user-selector-avatar-placeholder ${disabled ? "disabled" : ""}`}
        >
          +
        </Avatar>
      </Tooltip>
    );

    return (
      <>
        <Box
          ref={triggerRef}
          onClick={handleOpen}
          sx={{ display: "inline-flex", alignItems: "center", cursor: disabled ? "not-allowed" : "pointer" }}
        >
          {avatarContent}
        </Box>
        {dropdown}
      </>
    );
  }

  /** VARIANT: ICON (used in inline chips) */
  if (variant === "icon") {
    const iconContent = selectedItems.length > 0 ? (
      <>
        <Box
        className="tooltip-wrapper"
        sx={{display: "inline-flex", alignItems: "center", gap: "6px" }}>
        <Box sx={{ position: "relative", display: "inline-flex" }}>
        <Avatar
          src={selectedItems[0]?.pictureUrl || selectedItems[0]?.avatar_url || selectedItems[0]?.picture || ""}
          alt={selectedItems[0]?.name ?? selectedItems[0]?.email}
          sx={{ width: 18, height: 18, fontSize: "12px",
            bgcolor: !selectedItems?.[0]?.pictureUrl 
            ? getAvatarColor(selectedItems?.[0]?.email || selectedItems?.[0]?.name || "")
            : "",
          }}
        >
          {!selectedItems[0]?.pictureUrl ? getAvatarInitials(selectedItems[0]?.email || selectedItems[0]?.name) : null}
        </Avatar>
        <Box
          onClick={(e) => {
            e.stopPropagation();
            onChange(null, removeField);
          }}
          className="cross-icon-inline"
          sx={{
            position: "absolute",
            top: "-4px",
            right: "-4px",
          }}
        >
          <Typography sx={{ fontSize: "8px", color: "#fff", fontWeight: "bold" }}>×</Typography>
        </Box>
        </Box>
        <Typography sx={{ fontSize: "12px", color: "#33333B", lineHeight: 1 }}>
          {selectedItems[0]?.name ?? selectedItems[0]?.email}
        </Typography>
        </Box>
      </>
    ) : (
      <img src={variantIcon} alt="select" className="inline-chip-icon" />
    );

    const wrappedWithTooltip =
      tooltipTitle && (variantIcon || value) ? (
        <Tooltip title={tooltipTitle} arrow placement="top">
          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
            {iconContent}
          </span>
        </Tooltip>
      ) : (
        <>{iconContent}</>
      );

    return (
      <>
        <Box
          ref={triggerRef}
          onClick={handleOpen}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: disabled ? "not-allowed" : "pointer",
            gap: "6px",
          }}
        >
          {wrappedWithTooltip}
        </Box>

        {dropdown}
      </>
    );
  }

  /**  VARIANT: DEFAULT (full box layout) */
  return (
    <>
      <DropdownBox onClick={handleOpen} error={error} disabled={disabled} className="assignee-box">
        {selectedItems.length > 0 ? (
          multi ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {selectedItems.slice(0, 2).map((item) => 
                item.type === "user" ? (
                <UserAvatar key={item.profileOrgRelationshipId} user={item} size={26} maxNameWidth={50} />
              ):(
                <Box
                  key={item.id}
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: 1,
                    bgcolor: getAvatarColor(item.name),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  {getAvatarInitials(item.name)}
                </Box>
              ))}
              {selectedItems.length > 2 && (
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  +{selectedItems.length - 2}
                </Box>
              )}
            </Box>
          ) : (
            <Box 
            className="cell-hover-parent" 
            sx={{ position: "relative" }}
            >
              <UserAvatar 
                user={selectedItems[0]} 
                showName 
                maxNameWidth={50} 
                size={26} 
              />
              {!disabled && (
              <Box
                className="cross-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(null, removeField);
                }}
                sx={{
                  top: -4,
                  right: 50,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ fontSize: "12px", color: "#fff", fontWeight: "bold" }}>×</Typography>
              </Box>
              )}
            </Box>
          )
        ) : (
          <span className="priority-label">Select</span>
        )}
      </DropdownBox>

      {dropdown}
    </>
  );
};

export default UserSelector;
